import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchSymbolSuggestions, fetchCompanyName } from '../data/dataService';

// List of excluded exchanges
const EXCLUDED_EXCHANGES = [
  'CBOE',
  'New York Stock Exchange Arca',
  'NASDAQ Global Market',
];

// Check if name contains ETF/ETC/ETP
const isETProduct = (name) => {
  const etProducts = ['etf', 'etc', 'etp', 'fund'];
  return etProducts.some((product) => name.toLowerCase().includes(product));
};

/**
 * Custom hook for symbol search with debounced typeahead.
 * Consolidates the two competing debounce mechanisms from the original
 * FunViz.js (inputQueue 3000ms timer + handleSymbolChange 500ms debounce)
 * into a single debounce with configurable delay.
 *
 * @param {Object} options
 * @param {number} [options.debounceDelay=500] - Debounce delay in milliseconds
 * @returns {Object} Symbol search state and handlers
 */
export const useSymbolSearch = ({ debounceDelay = 500 } = {}) => {
  const [symbol, setSymbol] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  const debounceTimerRef = useRef(null);

  // Tracks the symbol that the current `companyName` is valid for. When the user
  // types a new ticker (without picking a suggestion) this no longer matches
  // `symbol`, which is how `ensureCompanyName` knows the name needs resolving.
  // Starts empty (not 'AAPL') so the mount-time load — AAPL by default, or the
  // Screener's ?symbol= — always resolves its real name rather than short-
  // circuiting on a stale Apple seed.
  const nameSymbolRef = useRef('');

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Debounced API call for fetching suggestions.
   * Filters out excluded exchanges and ET products,
   * sorts by symbol length, and limits to 25 results.
   */
  const debouncedFetchSuggestions = useCallback(
    (value) => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        if (value.trim()) {
          try {
            const data = await fetchSymbolSuggestions(
              value.trim()
            );

            setSearchFailed(false);

            const filtered = data
              .filter(
                (item) =>
                  item &&
                  typeof item.symbol === 'string' &&
                  typeof item.name === 'string' &&
                  !EXCLUDED_EXCHANGES.includes(item.exchangeShortName) &&
                  !isETProduct(item.name)
              )
              .map((item) => ({
                symbol: item.symbol,
                name: item.name,
                exchange: item.exchange || item.exchangeShortName || '',
              }))
              .sort(
                (a, b) =>
                  a.symbol.replace('.', '').length -
                  b.symbol.replace('.', '').length
              )
              .slice(0, 25);

            if (filtered.length > 0) {
              setSuggestions(filtered);
            } else {
              setSuggestions([
                {
                  symbol: 'N/A',
                  name: 'No results found. Try a different search term.',
                  exchange: '',
                },
              ]);
            }
          } catch (error) {
            console.error('Error processing suggestions:', error);
            setSuggestions([]);
            setSearchFailed(true);
          }
        } else {
          setSuggestions([]);
          setSearchFailed(false);
        }
      }, debounceDelay);
    },
    [debounceDelay]
  );

  /**
   * Handle symbol input change.
   * Updates symbol immediately for UI responsiveness,
   * then debounces the API call for suggestions.
   */
  const handleSymbolChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSymbol(value);
      debouncedFetchSuggestions(value);
    },
    [debouncedFetchSuggestions]
  );

  /**
   * Handle suggestion selection.
   * Sets symbol and company name, clears suggestions, hides dropdown.
   */
  const handleSuggestionSelect = useCallback((item) => {
    setSymbol(item.symbol);
    setCompanyName(item.name);
    nameSymbolRef.current = item.symbol;
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  /**
   * Ensures `companyName` corresponds to the given symbol.
   *
   * If the name is already valid for this symbol (e.g. it was picked from the
   * dropdown), the current name is returned without a network call. Otherwise —
   * a directly typed ticker or a trending selection that only carries the
   * symbol — the name is resolved via the search proxy and stored.
   *
   * @param {string} sym - The symbol to ensure a company name for
   * @returns {Promise<string>} The resolved company name
   */
  const ensureCompanyName = useCallback(
    async (sym) => {
      const target = (sym || '').trim();
      if (nameSymbolRef.current?.toUpperCase() === target.toUpperCase()) {
        return companyName;
      }
      const name = await fetchCompanyName(target);
      setCompanyName(name);
      nameSymbolRef.current = target;
      return name;
    },
    [companyName]
  );

  return {
    symbol,
    setSymbol,
    companyName,
    setCompanyName,
    suggestions,
    showSuggestions,
    setShowSuggestions,
    searchFailed,
    handleSymbolChange,
    handleSuggestionSelect,
    ensureCompanyName,
  };
};
